import { LocalNotifications } from "@capacitor/local-notifications";
import { Capacitor } from "@capacitor/core";

// A copy da notificação vivia aqui, num Record de três idiomas escrito à mão —
// invisível para o i18n:check, para a contagem de chaves e para o tradutor.
// Agora vem dos arquivos de locale como qualquer outro texto do app, e o
// chamador injeta o `t` (este módulo é chamado de fora do React).
export interface FocusNotificationCopy {
  title: string;
  body: string;
}

/**
 * Generate a stable int32 notification id derived from the current epoch
 * millisecond. LocalNotifications requires a 32-bit signed int.
 */
function makeId(): number {
  return Date.now() & 0x7fffffff;
}

/**
 * Ask the OS for notification permission. Resolves to true if granted.
 * Web fallback: returns false (no scheduling possible).
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const status = await LocalNotifications.requestPermissions();
    return status.display === "granted";
  } catch (e) {
    console.error("[focus/notifications] permission request failed", e);
    return false;
  }
}

/**
 * Schedule a local notification to fire when the focus timer ends.
 * Returns the scheduled id (so the caller can cancel later) or null
 * when running on web / permission denied / scheduling fails.
 */
export async function scheduleTimerEndNotification(
  durationMs: number,
  copy: FocusNotificationCopy,
): Promise<{ id: number } | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const id = makeId();
    await LocalNotifications.schedule({
      notifications: [
        {
          id,
          title: copy.title,
          body: copy.body,
          schedule: { at: new Date(Date.now() + durationMs) },
        },
      ],
    });
    return { id };
  } catch (e) {
    console.error("[focus/notifications] schedule failed", e);
    return null;
  }
}

/** Cancel a previously scheduled notification. Safe to call with stale ids. */
export async function cancelTimerEndNotification(id: number): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id }] });
  } catch (e) {
    console.error("[focus/notifications] cancel failed", e);
  }
}
